import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useOrganizationProfileCompleteness } from "@/hooks/use-organization-profile-notifications";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, AlertCircle, ChevronDown, ChevronUp, MapPin, X, Copy, ExternalLink } from "lucide-react";
import { useSyncOrganizationProfile } from "@/hooks/use-sync-organization-profile";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import { AvatarUpload } from "@/components/crm/AvatarUpload";
import { ShowcaseQRCode } from "@/components/organization/ShowcaseQRCode";
import { ShowcaseAccessCodeSettings } from "@/components/organization/ShowcaseAccessCodeSettings";
import { normalizeShowcaseAccessCode } from "@/utils/showcaseAccess";

interface OrganisationData {
  name: string;
  website?: string;
  industry?: string;
  size?: string;
  country?: string;
  company_id?: string;
  division?: string;
  logo_url?: string;
  opening_times?: string;
  description?: string;
  address?: string;
  color_palette?: {
    primary: string;
    secondary: string;
    accent: string;
  };
  marketing_banner_url?: string;
  image_1_url?: string;
  image_2_url?: string;
  image_3_url?: string;
  google_places_id?: string;
  visibility?: 'public' | 'private';
  custom_workflow_id?: string;
  custom_workflow_description?: string;
  showcase_access_code?: string;
  showcase_access_code_enabled?: boolean;
}

const privateIndustryOptions = [
  "Technology", "Healthcare", "Finance", "Education", "Retail", "Manufacturing",
  "Construction", "Real Estate", "Transportation", "Energy", "Agriculture",
  "Entertainment", "Hospitality", "Professional Services", "Non-profit", "Other"
];

const publicIndustryOptions = [
  "Golf Course", "Fishing", "Events", "Beaches", "Trails", "Parking", "Restaurants", "Parks"
];

const sizeOptions = [
  "1-10", "11-50", "51-200", "201-500", "501-1000", "1001-5000", "5000+"
];

export default function OrganisationProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  

  // Check user role
  const { data: userRole } = useQuery({
    queryKey: ['user-role', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase.rpc('get_user_role', {
        user_id: user.id
      });
      return data;
    },
    enabled: !!user?.id,
  });

  const isAdmin = userRole === 'admin' || userRole === 'superuser';

  // Get user's organization
  const { data: userOrganization, isLoading: isUserOrgLoading } = useQuery({
    queryKey: ['user-organization', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('user_roles')
        .select('organization_id')
        .eq('user_id', user.id)
        .maybeSingle();
      return data?.organization_id || null;
    },
    enabled: !!user?.id,
  });

  // Get organization details
  const { data: organization, isLoading } = useQuery({
    queryKey: ['organization-details', userOrganization],
    queryFn: async () => {
      if (!userOrganization) return null;
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', userOrganization)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!userOrganization,
  });

  // Get organization profile completeness
  const { data: completeness } = useOrganizationProfileCompleteness(userOrganization || '');

  const { register, handleSubmit, setValue, watch, reset, control } = useForm<OrganisationData>();
  
  // Watch logo_url for AvatarUpload component
  const watchedLogoUrl = watch('logo_url');
  
  // State for controlled Select components to prevent array values
  const [formState, setFormState] = useState<{
    industry: string;
    size: string;
    visibility: 'public' | 'private';
  }>({
    industry: '',
    size: '',
    visibility: 'private'
  });
  
  // State for visibility confirmation dialog
  const [visibilityConfirmDialog, setVisibilityConfirmDialog] = useState<{
    isOpen: boolean;
    pendingValue: 'public' | 'private' | null;
  }>({
    isOpen: false,
    pendingValue: null
  });
  
  // State for collapsible sections
  const [isBrandSectionExpanded, setIsBrandSectionExpanded] = useState(false);
  const [isAdditionalInfoExpanded, setIsAdditionalInfoExpanded] = useState(false);
  const [isCustomAiWorkflowExpanded, setIsCustomAiWorkflowExpanded] = useState(false);
  
  
  // Ref for scrolling to address field
  const addressFieldRef = useRef<HTMLTextAreaElement>(null);
  
  // Sync organization profile to BuildShip
  const syncOrganizationProfile = useSyncOrganizationProfile();

  // Initialize form with organization data
  useEffect(() => {
    if (organization) {
      const colorPalette = organization.color_palette && 
        typeof organization.color_palette === 'object' && 
        !Array.isArray(organization.color_palette) ? 
        organization.color_palette as { primary?: string; secondary?: string; accent?: string } : 
        { primary: '#000000', secondary: '#666666', accent: '#0066cc' };

      // Safely extract and validate string values to prevent arrays
      const safeIndustry = Array.isArray(organization.industry) 
        ? (organization.industry.length > 0 ? String(organization.industry[0]) : '') 
        : (organization.industry || '');
      
      const safeSize = Array.isArray(organization.size) 
        ? (organization.size.length > 0 ? String(organization.size[0]) : '') 
        : (organization.size || '');
      
      const safeVisibility = Array.isArray(organization.visibility)
        ? (organization.visibility.length > 0 ? String(organization.visibility[0]) : 'private')
        : (organization.visibility || 'private');

      // Update controlled state
      setFormState({
        industry: safeIndustry,
        size: safeSize,
        visibility: (safeVisibility as 'public' | 'private') || 'private'
      });

      reset({
        name: organization.name || '',
        website: organization.website || '',
        industry: safeIndustry,
        size: safeSize,
        country: organization.country || '',
        company_id: organization.company_id || `ORG-${organization.id?.slice(0, 8).toUpperCase()}`,
        division: organization.division || '',
        logo_url: organization.logo_url || '',
        opening_times: organization.opening_times || '',
        description: organization.description || '',
        address: organization.address || '',
        marketing_banner_url: organization.marketing_banner_url || '',
        image_1_url: organization.image_1_url || '',
        image_2_url: organization.image_2_url || '',
        image_3_url: organization.image_3_url || '',
        google_places_id: organization.google_places_id || '',
        visibility: (safeVisibility as 'public' | 'private') || 'private',
        custom_workflow_id: (organization as any).custom_workflow_id || '',
        custom_workflow_description: (organization as any).custom_workflow_description || '',
        showcase_access_code: (organization as any).showcase_access_code || '',
        showcase_access_code_enabled: !!(organization as any).showcase_access_code_enabled,
        color_palette: {
          primary: colorPalette.primary || '#000000',
          secondary: colorPalette.secondary || '#666666',
          accent: colorPalette.accent || '#0066cc'
        }
      });
      
    }
  }, [organization, reset]);

  // Helper function to safely convert values to database-compatible format
  const sanitizeForDatabase = (value: any, fieldName: string): string | null => {
    console.log(`🔍 Sanitizing field '${fieldName}':`, { value, type: typeof value, isArray: Array.isArray(value) });
    
    // Handle null, undefined, or empty string
    if (value === null || value === undefined || value === '') {
      console.log(`✅ Field '${fieldName}' converted to null`);
      return null;
    }
    
    // Handle arrays (this should not happen, but let's catch it)
    if (Array.isArray(value)) {
      console.error(`❌ Field '${fieldName}' is an array:`, value);
      return value.length > 0 ? String(value[0]) : null;
    }
    
    // Handle objects (should not happen for these fields)
    if (typeof value === 'object') {
      console.error(`❌ Field '${fieldName}' is an object:`, value);
      return JSON.stringify(value);
    }
    
    // Convert to string
    const stringValue = String(value).trim();
    console.log(`✅ Field '${fieldName}' converted to string:`, stringValue);
    return stringValue || null;
  };

  // Update organization mutation
  const updateOrganization = useMutation({
    mutationFn: async (data: OrganisationData) => {
      console.log('🔄 Starting organization update...', { userOrganization, data });
      console.log('🔍 Raw form data types:', Object.keys(data).reduce((acc, key) => {
        acc[key] = { value: data[key], type: typeof data[key], isArray: Array.isArray(data[key]) };
        return acc;
      }, {}));
      
      if (!userOrganization) {
        console.error('❌ No organization found for user');
        throw new Error('No organization found');
      }
      
      console.log('💾 Updating organization in database...');
      
      // Phase 1: Explicit String Validation with detailed logging
      const dbUpdateData = {
        name: sanitizeForDatabase(data.name, 'name'),
        website: sanitizeForDatabase(data.website, 'website'),
        industry: sanitizeForDatabase(data.industry, 'industry'),
        size: sanitizeForDatabase(data.size, 'size'),
        company_id: sanitizeForDatabase(data.company_id, 'company_id'),
        division: sanitizeForDatabase(data.division, 'division'),
        logo_url: sanitizeForDatabase(data.logo_url, 'logo_url'),
        opening_times: sanitizeForDatabase(data.opening_times, 'opening_times'),
        description: sanitizeForDatabase(data.description, 'description'),
        address: sanitizeForDatabase(data.address, 'address'),
        marketing_banner_url: sanitizeForDatabase(data.marketing_banner_url, 'marketing_banner_url'),
        image_1_url: sanitizeForDatabase(data.image_1_url, 'image_1_url'),
        image_2_url: sanitizeForDatabase(data.image_2_url, 'image_2_url'),
        image_3_url: sanitizeForDatabase(data.image_3_url, 'image_3_url'),
        google_places_id: sanitizeForDatabase(data.google_places_id, 'google_places_id'),
        visibility: sanitizeForDatabase(data.visibility, 'visibility'),
        country: sanitizeForDatabase(data.country, 'country'),
        custom_workflow_id: sanitizeForDatabase(data.custom_workflow_id, 'custom_workflow_id'),
        custom_workflow_description: sanitizeForDatabase(data.custom_workflow_description, 'custom_workflow_description'),
        showcase_access_code: normalizeShowcaseAccessCode(data.showcase_access_code) || null,
        showcase_access_code_enabled: !!data.showcase_access_code_enabled && !!normalizeShowcaseAccessCode(data.showcase_access_code),
        color_palette: data.color_palette, // Keep as-is for JSONB
        updated_at: new Date().toISOString()
      };
      
      // Phase 3: Database Safety Layer - Final validation
      console.log('🛡️ Final database payload validation...');
      Object.keys(dbUpdateData).forEach(key => {
        const value = dbUpdateData[key];
        if (key !== 'color_palette' && key !== 'updated_at') { // Skip JSONB and timestamp fields
          if (Array.isArray(value)) {
            console.error(`🚨 CRITICAL: Field '${key}' is still an array after sanitization:`, value);
            dbUpdateData[key] = null; // Force to null to prevent array literal error
          }
        }
      });
      
      console.log('📝 Final database update payload:', dbUpdateData);
      console.log('🔍 Payload field types:', Object.keys(dbUpdateData).reduce((acc, key) => {
        acc[key] = typeof dbUpdateData[key];
        return acc;
      }, {}));
      
      const { data: updatedData, error } = await supabase
        .from('organizations')
        .update(dbUpdateData)
        .eq('id', userOrganization)
        .select()
        .single();
      
      if (error) {
        console.error('❌ Database update error:', error);
        throw error;
      }
      
      console.log('✅ Database update successful:', updatedData);
      return { originalData: data, updatedData };
    },
    onSuccess: async ({ originalData, updatedData }) => {
      console.log('🎉 Organization update successful, starting sync...');
      toast.success('Organisation profile updated successfully');
      
      // Invalidate queries to refresh UI and profile completeness
      queryClient.invalidateQueries({ queryKey: ['organization-details'] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['organization-profile-completeness', userOrganization] });
      queryClient.invalidateQueries({ queryKey: ['organization-profile-notifications'] });
      
      // Sync profile data to BuildShip with retry logic
      if (userOrganization) {
        try {
          await syncOrganizationProfile.mutateAsync({
            organizationId: userOrganization,
            profileData: originalData
          });
          console.log('✅ Full save and sync process completed successfully');
        } catch (syncError) {
          console.error('❌ Sync failed but database was updated:', syncError);
          toast.error('Profile saved but external sync failed - changes may not appear immediately in all systems');
        }
      }
    },
    onError: (error) => {
      console.error('💥 Organization update failed:', error);
      toast.error(`Failed to update organisation profile: ${error.message}`);
    },
    retry: 1,
    retryDelay: 1000,
  });

  // Check if user has access - admins get full access, organization members get limited access
  const isDataLoading = isLoading || isUserOrgLoading;
  const hasLimitedAccess = userOrganization && !isDataLoading;
  const hasFullAccess = isAdmin && !isDataLoading;
  
  // Redirect users who don't belong to any organization
  useEffect(() => {
    if (userRole && !isDataLoading && !hasLimitedAccess && !hasFullAccess) {
      navigate('/dashboard');
    }
  }, [userRole, isDataLoading, hasLimitedAccess, hasFullAccess, navigate]);

  // Phase 3: Client-side validation to prevent array values
  const validateFormData = (data: OrganisationData): string[] => {
    const errors: string[] = [];
    
    // Check for array values in string fields
    Object.entries(data).forEach(([key, value]) => {
      if (key !== 'color_palette' && Array.isArray(value)) {
        errors.push(`Field '${key}' contains invalid array value: ${JSON.stringify(value)}`);
      }
    });
    
    return errors;
  };

  const onSubmit = (data: OrganisationData) => {
    console.log('🔍 Form submission - validating data before submit:', data);
    
    // Validate form data for arrays
    const validationErrors = validateFormData(data);
    if (validationErrors.length > 0) {
      console.error('❌ Form validation failed:', validationErrors);
      toast.error(`Form validation failed: ${validationErrors[0]}`);
      return;
    }
    
    // Ensure controlled state values are used for select fields
    const sanitizedData = {
      ...data,
      industry: formState.industry,
      size: formState.size,
      visibility: formState.visibility
    };
    
    console.log('✅ Form validation passed, submitting sanitized data:', sanitizedData);
    updateOrganization.mutate(sanitizedData);
  };

  // Get industry options based on visibility
  const getIndustryOptions = () => {
    return formState.visibility === 'public' ? publicIndustryOptions : privateIndustryOptions;
  };

  if (!userRole || (!hasLimitedAccess && !hasFullAccess)) {
    return null;
  }

  if (isLoading) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <AppSidebar />
          <main className="flex-1">
            <div className="p-8 flex items-center justify-center h-64">
              <p className="text-muted-foreground">Loading organisation details...</p>
            </div>
          </main>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader
            {...PAGE_HEADERS.organisationProfile}
            actions={
              completeness && typeof completeness === 'object' && (
                <Badge 
                  className={`flex items-center gap-1 ${
                    (completeness as any).is_complete 
                      ? 'bg-[#DFEF97] text-black border-[#DFEF97] hover:bg-[#DFEF97]/90' 
                      : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                  }`}
                >
                  {(completeness as any).is_complete ? (
                    <>
                      <CheckCircle className="h-3 w-3" />
                      Complete
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-3 w-3" />
                      {Math.round((completeness as any).completion_percentage || 0)}% Complete
                    </>
                  )}
                </Badge>
              )
            }
          />
          <div className="p-8 space-y-8" style={{ paddingTop: "8.5rem" }}>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Privacy Settings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="visibility">Organisation Visibility</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        {formState.visibility === 'public' 
                          ? 'This organisation is visible to members of the public' 
                          : 'This organisation is only visible to authorised users'}
                      </p>
                    </div>
                     <div className="flex items-center space-x-2">
                       <Label htmlFor="visibility-switch">Private</Label>
                       <Switch
                         id="visibility-switch"
                         checked={formState.visibility === 'public'}
                         onCheckedChange={(checked) => {
                           const newVisibility = checked ? 'public' : 'private';
                           const currentVisibility = formState.visibility;
                           
                           // Show confirmation dialog if changing visibility
                           if (currentVisibility !== newVisibility) {
                             setVisibilityConfirmDialog({
                               isOpen: true,
                               pendingValue: newVisibility
                             });
                           }
                         }}
                       />
                        <Label htmlFor="visibility-switch">Public</Label>
                      </div>

                       {formState.visibility === 'public' && userOrganization && organization?.slug && (
                        <div className="mt-6 pt-6 border-t">
                          <Label className="text-sm font-medium">Public Showcase URL</Label>
                          <p className="text-sm text-muted-foreground mb-3">
                            Share this link to allow public access to your cameras
                          </p>
                          <div className="grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-4 items-start">
                            <div className="flex flex-col gap-2">
                              <div className="flex gap-2">
                                <Input
                                  readOnly
                                  value={`${window.location.origin}/showcase/${organization.slug}`}
                                  className="font-mono text-sm"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    navigator.clipboard.writeText(`${window.location.origin}/showcase/${organization.slug}`);
                                    toast.success("Showcase URL copied to clipboard");
                                  }}
                                >
                                  <Copy className="h-4 w-4 mr-2" />
                                  Copy
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => window.open(`/showcase/${organization.slug}`, '_blank')}
                                >
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  View
                                </Button>
                              </div>
                            </div>
                            <ShowcaseQRCode 
                              organizationId={organization.slug}
                              organizationName={organization.name}
                              size={200}
                            />
                          </div>

                          <ShowcaseAccessCodeSettings
                            enabled={!!watch('showcase_access_code_enabled')}
                            code={watch('showcase_access_code') || ''}
                            showcaseUrl={`${window.location.origin}/showcase/${organization.slug}`}
                            organizationName={organization.name}
                            onEnabledChange={(value) => setValue('showcase_access_code_enabled', value, { shouldDirty: true })}
                            onCodeChange={(value) => setValue('showcase_access_code', value, { shouldDirty: true })}
                          />
                        </div>
                      )}
                   </div>
                 </CardContent>
                </Card>

               {/* Visibility Confirmation Dialog */}
               <AlertDialog 
                 open={visibilityConfirmDialog.isOpen} 
                 onOpenChange={(open) => {
                   if (!open) {
                     setVisibilityConfirmDialog({ isOpen: false, pendingValue: null });
                   }
                 }}
               >
                 <AlertDialogContent>
                   <AlertDialogHeader>
                     <AlertDialogTitle>
                       {visibilityConfirmDialog.pendingValue === 'public' ? 'Make Organisation Public?' : 'Make Organisation Private?'}
                     </AlertDialogTitle>
                     <AlertDialogDescription>
                       {visibilityConfirmDialog.pendingValue === 'public' ? (
                         <>
                           <strong>Warning:</strong> Making your organisation public will have the following effects:
                           <ul className="list-disc pl-6 mt-2 space-y-1">
                             <li>Your organisation and its images may be visible to members of the public</li>
                             <li>Image retention period will be reduced from 90 to 30 days for all past and future images</li>
                             <li>This change affects all existing images in your organisation</li>
                           </ul>
                         </>
                       ) : (
                         <>
                           Making your organisation private will:
                           <ul className="list-disc pl-6 mt-2 space-y-1">
                             <li>Make your organisation only visible to authorised users</li>
                             <li>Increase image retention period from 30 to 90 days for all future images</li>
                             <li>Existing images will retain their current retention period</li>
                           </ul>
                         </>
                       )}
                     </AlertDialogDescription>
                   </AlertDialogHeader>
                   <AlertDialogFooter>
                     <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => {
                          if (visibilityConfirmDialog.pendingValue) {
                            setFormState(prev => ({ ...prev, visibility: visibilityConfirmDialog.pendingValue! }));
                            setValue('visibility', visibilityConfirmDialog.pendingValue);
                          }
                          setVisibilityConfirmDialog({ isOpen: false, pendingValue: null });
                        }}
                        className="bg-[#DFEF97] text-black hover:bg-[#DFEF97]/90"
                     >
                       {visibilityConfirmDialog.pendingValue === 'public' ? 'Make Public' : 'Make Private'}
                     </AlertDialogAction>
                   </AlertDialogFooter>
                 </AlertDialogContent>
               </AlertDialog>

              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Organisation Name *</Label>
                      <Input
                        id="name"
                        {...register('name', { required: true })}
                        placeholder="Enter organisation name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        type="url"
                        {...register('website')}
                        placeholder="https://example.com"
                      />
                    </div>
                  </div>
                  
                  {/* Address Field - Moved to prominent position */}
                  <div>
                    <Label htmlFor="address" className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Business Address
                    </Label>
                    <Textarea
                      ref={addressFieldRef}
                      id="address"
                      {...register('address')}
                      placeholder="Enter your complete business address including street, city, postal code, and country"
                      rows={3}
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      This address will be used for location-based features and business verification
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="industry">Industry</Label>
                      <Select 
                        value={formState.industry || ''} 
                        onValueChange={(value) => {
                          const newIndustry = value === 'none' ? '' : value;
                          setFormState(prev => ({ ...prev, industry: newIndustry }));
                          setValue('industry', newIndustry);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {getIndustryOptions().map((industry) => (
                            <SelectItem key={industry} value={industry}>
                              {industry}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="size">Company Size</Label>
                      <Select 
                        value={formState.size || ''} 
                        onValueChange={(value) => {
                          const newSize = value === 'none' ? '' : value;
                          setFormState(prev => ({ ...prev, size: newSize }));
                          setValue('size', newSize);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select company size" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {sizeOptions.map((size) => (
                            <SelectItem key={size} value={size}>
                              {size} employees
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="company_id">Company ID</Label>
                      <Input
                        id="company_id"
                        {...register('company_id')}
                        placeholder="Auto-generated company identifier"
                        readOnly
                        className="bg-muted cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        {...register('country')}
                        placeholder="e.g., United Kingdom"
                      />
                    </div>
                    <div>
                      <Label htmlFor="division">Division (Optional)</Label>
                      <Input
                        id="division"
                        {...register('division')}
                        placeholder="Division or department"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      {...register('description')}
                      placeholder="Brief description of the organisation"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label htmlFor="opening_times">Opening Times</Label>
                    <Input
                      id="opening_times"
                      {...register('opening_times')}
                      placeholder="e.g., Mon-Fri 9:00-17:00"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader 
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setIsBrandSectionExpanded(!isBrandSectionExpanded)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center">
                      Brand & Visual Identity 
                      <Badge variant="outline" className="ml-2">Optional</Badge>
                    </CardTitle>
                    {isBrandSectionExpanded ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </CardHeader>
                <div 
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isBrandSectionExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Organization Logo</Label>
                      <AvatarUpload
                        currentImageUrl={watchedLogoUrl || organization?.logo_url}
                        onImageChange={(imageUrl) => setValue('logo_url', imageUrl)}
                        fallbackText={organization?.name?.substring(0, 2).toUpperCase() || 'ORG'}
                        size="lg"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Upload an image (PNG, JPG, GIF up to 5MB) or keep using a URL
                      </p>
                    </div>
                    
                    <div>
                      <Label>Color Palette</Label>
                      <div className="grid grid-cols-3 gap-4 mt-2">
                        <div>
                          <Label htmlFor="primary_color">Primary Color</Label>
                          <Input
                            id="primary_color"
                            type="color"
                            value={watch('color_palette.primary') || '#000000'}
                            onChange={(e) => setValue('color_palette.primary', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label htmlFor="secondary_color">Secondary Color</Label>
                          <Input
                            id="secondary_color"
                            type="color"
                            value={watch('color_palette.secondary') || '#666666'}
                            onChange={(e) => setValue('color_palette.secondary', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label htmlFor="accent_color">Accent Color</Label>
                          <Input
                            id="accent_color"
                            type="color"
                            value={watch('color_palette.accent') || '#0066cc'}
                            onChange={(e) => setValue('color_palette.accent', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="marketing_banner_url">
                          Hero Image URL (Showcase Page)
                          <span className="text-xs text-muted-foreground ml-2">
                            Displays at top of public showcase page
                          </span>
                        </Label>
                        <Input
                          id="marketing_banner_url"
                          type="url"
                          {...register('marketing_banner_url')}
                          placeholder="https://example.com/banner.png"
                        />
                      </div>
                      <div>
                        <Label htmlFor="image_1_url">Image 1 URL</Label>
                        <Input
                          id="image_1_url"
                          type="url"
                          {...register('image_1_url')}
                          placeholder="https://example.com/image1.png"
                        />
                      </div>
                      <div>
                        <Label htmlFor="image_2_url">Image 2 URL</Label>
                        <Input
                          id="image_2_url"
                          type="url"
                          {...register('image_2_url')}
                          placeholder="https://example.com/image2.png"
                        />
                      </div>
                      <div>
                        <Label htmlFor="image_3_url">Image 3 URL</Label>
                        <Input
                          id="image_3_url"
                          type="url"
                          {...register('image_3_url')}
                          placeholder="https://example.com/image3.png"
                        />
                      </div>
                    </div>
                  </CardContent>
                </div>
              </Card>

              <Card>
                <CardHeader 
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setIsAdditionalInfoExpanded(!isAdditionalInfoExpanded)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center">
                      Additional Information
                      <Badge variant="outline" className="ml-2">Optional</Badge>
                    </CardTitle>
                    {isAdditionalInfoExpanded ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </CardHeader>
                <div 
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isAdditionalInfoExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <CardContent>
                    <div>
                      <Label htmlFor="google_places_id">Google Places ID</Label>
                      <Input
                        id="google_places_id"
                        {...register('google_places_id')}
                        placeholder="Google Places identifier for location data"
                      />
                    </div>
                  </CardContent>
                </div>
              </Card>

              <Card>
                <CardHeader 
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setIsCustomAiWorkflowExpanded(!isCustomAiWorkflowExpanded)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center">
                      Custom AI Workflow
                      <Badge variant="outline" className="ml-2">Optional</Badge>
                    </CardTitle>
                    {isCustomAiWorkflowExpanded ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </CardHeader>
                <div 
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isCustomAiWorkflowExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <CardContent className="space-y-4">
                    {(() => {
                      const cwId = watch('custom_workflow_id');
                      const cwDesc = watch('custom_workflow_description');
                      const isEmpty = !cwId && !cwDesc;
                      return (
                        <>
                          <div>
                            <Label>Custom Workflow ID</Label>
                            <div className="mt-1 rounded-md border border-input bg-muted/50 px-3 py-2 text-sm font-mono break-all min-h-10 flex items-center text-left justify-start">
                              {cwId || <span className="text-muted-foreground italic font-sans">Not set</span>}
                            </div>
                          </div>
                          <div>
                            <Label>Custom Workflow Description</Label>
                            <div className="mt-1 rounded-md border border-input bg-muted/50 px-3 py-2 text-sm whitespace-pre-wrap min-h-10 text-left">
                              {cwDesc || <span className="text-muted-foreground italic">Not set</span>}
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {isEmpty
                              ? "Set by your account manager. Contact support to update."
                              : "Managed by Snapshot Live staff."}
                          </p>
                        </>
                      );
                    })()}
                  </CardContent>
                </div>
              </Card>

              <div className="flex justify-end">
                <Button 
                  type="submit" 
                  disabled={updateOrganization.isPending}
                  className="min-w-32 bg-[#DFEF97] text-black hover:bg-[#DFEF97]/90"
                >
                  {updateOrganization.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}